import { useEffect, useState } from 'react';
import { NoteIcon, Plus, Warning } from 'assets/icons';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import PanelCard, { SECTION_TITLE_CLASS } from 'components/card/PanelCard';
import Tooltip from 'components/tooltips/Tooltips';
import { isDefaultCoefficientRanges } from 'helper/coefficientRange';
import { mostRecent } from 'helper/lastUpdate';
import PostIt, {
  POST_IT_TITLE_COLOR,
  noteAfterSave,
} from 'components/postIt/PostIt';
import CompanyPostItDialog from 'components/postIt/CompanyPostItDialog';
import useCardFlash, { FLASH_CLASS } from 'hooks/useCardFlash';
import useText from 'hooks/useText';
import LastUpdateIcon from 'components/lastUpdateIcon/LastUpdateIcon';
import CoefficientsCard from 'components/remuneration/CoefficientsCard';
import RemunerationRow from 'components/remuneration/RemunerationRow';
import SlideOverCompanyBonusAndCompensation from './slideOverCompanyBonusAndCompensation';

const isSet = (value) => value !== undefined && value !== null && value !== '';
const increase = (percentage) => (isSet(percentage) ? `+${percentage} %` : '—');

/**
 * The remuneration conditions of a company, in the prototype's three cards:
 * the billing coefficients (their grid opens with `setIsShowCoeff`), the
 * increases (each line opens its own modal) and the benefits (meal vouchers,
 * bonuses), then the post-it. A section still on its default values carries
 * the alert icon, and the clock dates its last change.
 */
const SlideOverCompanyRemunerationConditions = ({
  companyInfo,
  setCompanyInfo,
  setIsShowCoeff,
  setIsCoeffSend,
  onSaved,
}) => {
  const { toggle } = useModal();
  const { setModalData, modalDataReturn, setModalDataReturn, userData } =
    UseContext();
  const { text } = useText('remuneration');
  const { flashCard, isFlashed, registerCard } = useCardFlash();

  // A complete grid tells the panel the coefficients may be sent.
  useEffect(() => {
    const ranges = companyInfo?.coefficients ?? [];
    if (
      ranges.length &&
      ranges.every(
        (range) => range?.management !== null && range?.delegation !== null,
      )
    ) {
      setIsCoeffSend(true);
    }
  }, [companyInfo]); // the setter comes from the panel and never changes

  const openModal = (type) => {
    setModalData({ idCompany: companyInfo?._id });
    toggle(type);
  };

  // nightHours/overtimes default to [] (never undefined), and sundayHours/
  // holidayHours to an empty sub-document: what counts is a value set.
  const overtimes = companyInfo?.overtimes ?? [];
  const nightHours = companyInfo?.nightHours ?? [];
  const sundayPercentage = companyInfo?.sundayHours?.percentage;
  const holidayPercentage = companyInfo?.holidayHours?.percentage;
  const hasHolidayHours = isSet(holidayPercentage);

  // The slug ("lundi_de_pentecote") is never shown: getCompanyInfo names it.
  // A day set but not named (referential unreachable) shows no line rather
  // than « Aucun », which would report a missing day that is there.
  const solidarityDayLabel = hasHolidayHours
    ? companyInfo?.solidarityDayLabel
    : null;
  const hasSolidarityDay = Boolean(companyInfo?.holidayHours?.solidarityDay);

  // No flag says a section is still on its default values: it is inferred.
  const isDefault = {
    coefficients: isDefaultCoefficientRanges(
      companyInfo?.coefficients,
      companyInfo?.defaultCoefficients,
    ),
    overtimes: !overtimes.length,
    nightHours: !nightHours.length,
    sundayHours: !isSet(sundayPercentage),
    holidayHours: !hasHolidayHours,
  };
  // Tooltip rather than a native title: the icon has no text of its own.
  const defaultIcon = (section) =>
    isDefault[section] ? (
      <Tooltip text={text('defaultData')} placement="top">
        <span className="flex">
          <Warning wh={18} color="#975612" />
        </span>
      </Tooltip>
    ) : null;
  const lastUpdates = companyInfo?.lastUpdates ?? {};
  const lastUpdateOf = (key) =>
    lastUpdates[`modification_${key}`] ?? lastUpdates[`ajout_${key}`];

  useEffect(() => {
    if (!modalDataReturn) return;
    const updates = {
      night_hours_updated: [
        'nightHours',
        { nightHours: modalDataReturn.nightHours },
      ],
      overtimes_updated: [
        'overtimes',
        { overtimes: modalDataReturn.overtimes },
      ],
      sunday_hours_updated: [
        'sundayHours',
        { sundayHours: modalDataReturn.sundayHours },
      ],
      // The label travels with the change, or the card would name the old day.
      holiday_hours_updated: [
        'holidayHours',
        {
          holidayHours: modalDataReturn.holidayHours,
          solidarityDayLabel: modalDataReturn.solidarityDayLabel ?? null,
        },
      ],
    };
    const update = updates[modalDataReturn.type];
    if (update && Object.values(update[1])[0]) {
      setCompanyInfo((previous) => ({ ...previous, ...update[1] }));
      flashCard(update[0]);
      // The save endpoints return the field only: the refetch fills lastUpdates.
      onSaved?.();
    }
    setModalDataReturn(null);
  }, [modalDataReturn]);

  // Only the empty state's: a written note opens its own editor.
  const [isPostItEditorOpen, setIsPostItEditorOpen] = useState(false);
  const savePostIt = (nextNote) => {
    setCompanyInfo((previous) => ({ ...previous, post_it: nextNote }));
    onSaved?.();
  };

  const flashed = (section) => (isFlashed(section) ? FLASH_CLASS : '');
  const majorations = [
    ...(overtimes.length
      ? overtimes.map((overtime, index) => ({
          key: `overtime-${index}`,
          section: 'overtimes',
          label: index ? '' : text('overtime'),
          value: increase(overtime.percentage),
          text: text('overtimeText', { hours: overtime.start }),
          modal: 'overtime_company',
        }))
      : [
          {
            key: 'overtime',
            section: 'overtimes',
            label: text('overtime'),
            value: '—',
            modal: 'overtime_company',
          },
        ]),
    ...(nightHours.length
      ? nightHours.map((night, index) => ({
          key: `night-${index}`,
          section: 'nightHours',
          label: index ? '' : text('night'),
          value: increase(night.percentage),
          text: text('nightText', { start: night.start, end: night.end }),
          modal: 'night_hours_company',
        }))
      : [
          {
            key: 'night',
            section: 'nightHours',
            label: text('night'),
            value: '—',
            modal: 'night_hours_company',
          },
        ]),
    {
      key: 'sunday',
      section: 'sundayHours',
      label: text('sunday'),
      value: increase(sundayPercentage),
      modal: 'sunday_hours_company',
    },
    {
      key: 'holiday',
      section: 'holidayHours',
      label: text('holiday'),
      value: increase(holidayPercentage),
      modal: 'holiday_hours_company',
    },
    hasHolidayHours &&
      (solidarityDayLabel || !hasSolidarityDay) && {
        key: 'solidarity',
        section: 'holidayHours',
        label: text('solidarity'),
        value: solidarityDayLabel || text('none'),
        modal: 'holiday_hours_company',
        noAlert: true,
      },
  ].filter(Boolean);
  // The alert sits once per section, on its first line.
  const firstOfSection = new Set();

  return (
    <>
      <CoefficientsCard
        coefficients={companyInfo?.coefficients}
        attestation={companyInfo?.coefficientsAttestation}
        isDefault={isDefault.coefficients}
        cardRef={registerCard('coefficients')}
        className={flashed('coefficients')}
        onEdit={() => setIsShowCoeff(true)}
        badge={
          <span className="flex items-center gap-2">
            <LastUpdateIcon
              lastUpdate={lastUpdateOf('coefficient_facturation')}
            />
            {defaultIcon('coefficients')}
          </span>
        }
      />
      <PanelCard
        title={text('majorationsTitle')}
        titleClassName={SECTION_TITLE_CLASS}
        collapsible
        badge={
          <LastUpdateIcon
            lastUpdate={mostRecent(
              lastUpdateOf('heure_supplementaire'),
              lastUpdateOf('heure_nuit'),
              lastUpdateOf('heure_dimanche'),
              lastUpdateOf('heure_jour_ferie_travailles'),
            )}
          />
        }
        contentClassName="py-1"
      >
        {majorations.map((row) => {
          const first = !row.noAlert && !firstOfSection.has(row.section);
          if (first) firstOfSection.add(row.section);
          return (
            <RemunerationRow
              key={row.key}
              rowRef={first ? registerCard(row.section) : undefined}
              className={flashed(row.section)}
              label={row.label}
              value={row.value}
              text={row.text}
              onOpen={() => openModal(row.modal)}
              trailing={first ? defaultIcon(row.section) : null}
            />
          );
        })}
      </PanelCard>
      {/* Bonuses and meal vouchers are remuneration conditions like the rest. */}
      <SlideOverCompanyBonusAndCompensation
        companyInfo={companyInfo}
        setCompanyInfo={setCompanyInfo}
        onSaved={onSaved}
      />
      {/* Every copy of the note edits it; only this one can add it. */}
      <PostIt
        note={companyInfo?.post_it}
        companyName={companyInfo?.name_commercial ?? companyInfo?.reason_social}
        companyId={companyInfo?._id}
        onSaved={savePostIt}
      />
      {!companyInfo?.post_it?.content?.trim() && (
        <button
          type="button"
          // The note's own fill as this row's border, over a lighter tint: a
          // post-it waiting to be written, never one already written.
          className="rounded-xl border border-[#F0E18C] bg-[#FFF3B0] p-3 text-left"
          onClick={() => setIsPostItEditorOpen(true)}
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 text-[#5C4D0A]">
                <NoteIcon wh={16} color={POST_IT_TITLE_COLOR} />
                <span>Post-it</span>
              </div>
              <div className="text-[#7A6828]">
                Une consigne à faire remonter sur les relevés et les contrats
              </div>
            </div>
            <Plus wh={20} color="#5C4D0A" />
          </div>
        </button>
      )}
      {isPostItEditorOpen && (
        <CompanyPostItDialog
          companyId={companyInfo?._id}
          onClose={() => setIsPostItEditorOpen(false)}
          onSaved={(content) =>
            savePostIt(noteAfterSave(companyInfo?.post_it, content, userData))
          }
        />
      )}
    </>
  );
};

export default SlideOverCompanyRemunerationConditions;
